import type { Cell, Row, Workbook, Worksheet } from 'exceljs';
import type { StatisticCell } from '../statisticCells';
import type {
  StatisticsExportRow,
  StatisticsExportSection,
  StatisticsExportSnapshot,
} from './statisticsExportModel';
import {
  addStatisticsModeSelector,
  getStatisticsCellFormat,
} from './statisticsWorkbookModes';

const COLORS = {
  blue: 'FF1757B8',
  paleBlue: 'FFEAF1FF',
  white: 'FFFFFFFF',
  border: 'FFD5D9E0',
} as const;

const wrappedHeight = (
  label: string,
  depth: number,
  fontSize: number,
): number => {
  const lineWidth = Math.max(20, 64 - depth * 3);
  const lines = label
    .split('\n')
    .reduce(
      (total, line) => total + Math.max(1, Math.ceil(line.length / lineWidth)),
      0,
    );
  return Math.max(20, lines * (fontSize + 3) + 6);
};

const setMetricCell = (cell: Cell, metric: StatisticCell) => {
  cell.value = metric.value;
  cell.numFmt = getStatisticsCellFormat(metric);
};

const styleDataRow = (row: Row, data: StatisticsExportRow) => {
  row.height = wrappedHeight(data.label, data.depth, 9);
  [2, 3, 4].forEach((column) => {
    const cell = row.getCell(column);
    cell.font = { name: 'Arial', size: 9, bold: data.emphasized };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: COLORS.white },
    };
    cell.border = { bottom: { style: 'thin', color: { argb: COLORS.border } } };
    cell.alignment = { vertical: 'middle', horizontal: 'right' };
  });
  row.getCell(2).alignment = {
    vertical: 'middle',
    horizontal: 'left',
    wrapText: true,
    indent: data.depth,
  };
};

const addSectionTitle = (
  sheet: Worksheet,
  section: StatisticsExportSection,
) => {
  const { title, modeLabel } = section;
  const row = sheet.addRow([title]);
  sheet.mergeCells(row.number, 1, row.number, modeLabel ? 3 : 4);
  const cell = row.getCell(1);
  cell.numFmt = '@';
  cell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: COLORS.blue },
  };
  cell.font = {
    name: 'Arial',
    size: 10,
    bold: true,
    color: { argb: COLORS.white },
  };
  cell.alignment = {
    vertical: 'middle',
    horizontal: 'left',
    wrapText: true,
    indent: 1,
  };
  if (modeLabel) {
    const modeCell = row.getCell(4);
    modeCell.value = modeLabel;
    modeCell.numFmt = '@';
    modeCell.fill = cell.fill;
    modeCell.font = cell.font;
    modeCell.alignment = {
      vertical: 'middle',
      horizontal: 'right',
      indent: 1,
    };
  }
  row.height = wrappedHeight(title, 0, 10);
  return row;
};

const addTableHeader = (sheet: Worksheet) => {
  const row = sheet.addRow([null, 'Показатель', 'Значение', 'Доля от всех']);
  row.height = 24;
  [1, 2, 3, 4].forEach((column) => {
    const cell = row.getCell(column);
    cell.numFmt = '@';
    cell.font = {
      name: 'Arial',
      size: 10,
      bold: true,
      color: { argb: COLORS.blue },
    };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: COLORS.paleBlue },
    };
    cell.border = {
      bottom: { style: 'thin', color: { argb: COLORS.blue } },
    };
    cell.alignment = {
      vertical: 'middle',
      horizontal: column <= 2 ? 'left' : 'right',
      wrapText: true,
    };
  });
};

export const buildStatisticsWorkbook = (
  workbook: Workbook,
  snapshot: StatisticsExportSnapshot,
): Workbook => {
  workbook.calcProperties.fullCalcOnLoad = true;
  const sheet = workbook.addWorksheet(snapshot.sheetName);
  [5, 68, 18, 18].forEach((width, index) => {
    sheet.getColumn(index + 1).width = width;
  });
  snapshot.sections.forEach((section, index) => {
    if (index > 0) sheet.addRow([]);
    const title = addSectionTitle(sheet, section);
    const writeModeRow = addStatisticsModeSelector(
      sheet,
      section,
      title.number,
    );
    addTableHeader(sheet);
    section.rows.forEach((data, rowIndex) => {
      const row = sheet.addRow([null, data.label]);
      row.getCell(2).numFmt = '@';
      setMetricCell(row.getCell(3), data.cells.value);
      setMetricCell(row.getCell(4), data.cells.percent);
      styleDataRow(row, data);
      if (writeModeRow) {
        const variants = writeModeRow(row, data, rowIndex);
        row.height = Math.max(
          ...variants.map((variant) =>
            wrappedHeight(variant.label, variant.depth, 9),
          ),
        );
      }
    });
  });
  if (sheet.rowCount > 0) sheet.pageSetup.printArea = `A1:D${sheet.rowCount}`;
  return workbook;
};
